import React, { useEffect, useState } from "react";
import { Form, Table, Input, Button, Col, Row, Spin, Tree } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../assets/back-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { fetchUserHomeworks } from "./redux/actions";
import moment from "moment";

const HomeworkHistory = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const [search, setSearch] = useState({});
  const dispatch = useDispatch();

  const { homeworks: rawHomeworks, fetchHomeworksLoading } = useSelector(
    (state) => state.userManagerReducer
  );

  const extractOutlines = (outlines) => {
    if (!outlines) return [];
    let result = [];

    outlines.forEach((outline) => {
      result.push(outline.title);
      if (outline.children && outline.children.length > 0) {
        result = result.concat(extractOutlines(outline.children));
      }
    });

    return result;
  };

  const homeworks = rawHomeworks.flatMap((item) =>
    item.homeworks.map((hw) => ({
      ...hw,
      curriculumName: item.curriculum?.name,
      // studentName: `${item.student?.first_name ?? ""} ${item.student?.last_name ?? ""}`,
      outlineText: extractOutlines(hw.outlines).join(", "),
      outlines: hw.outlines,
    }))
  );

  const filteredHomeworks = homeworks.filter(
    (hw) =>
      (!search.curriculumName ||
        hw.curriculumName
          ?.toLowerCase()
          .includes(search.curriculumName.toLowerCase())) &&
      (!search.outlineText ||
        hw.outlineText
          ?.toLowerCase()
          .includes(search.outlineText.toLowerCase())) &&
      // (!search.studentName || hw.studentName?.toLowerCase().includes(search.studentName.toLowerCase())) &&
      (!search.status ||
        hw.status
          ?.join(", ")
          .toLowerCase()
          .includes(search.status.toLowerCase())) &&
      (!search.created_on ||
        moment(hw.created_on)
          .format("DD MMM YYYY hh:mm A")
          .toLowerCase()
          .includes(search.created_on.toLowerCase())) &&
      (!search.due_on ||
        moment(hw.due_on)
          .format("DD MMM YYYY hh:mm A")
          .toLowerCase()
          .includes(search.due_on.toLowerCase())) &&
      (!search.submitted_on ||
        moment(hw.submitted_on)
          .format("DD MMM YYYY hh:mm A")
          .toLowerCase()
          .includes(search.submitted_on.toLowerCase())) &&
      (!search.content ||
        hw.outlineText?.toLowerCase().includes(search.content.toLowerCase()))
  );

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => {
      dispatch(fetchUserHomeworks({ user_id, ...search }));
    }, 500)
  );

  useEffect(() => {
    if (user_id) {
      dispatch(fetchUserHomeworks({ user_id }));
    }
  }, [dispatch, user_id]);

  const handleSearch = (key, value, func = null) => {
    let updatedState = { ...search, [key]: value };
    setSearch(updatedState);
    if (typeof func === "function") {
      func();
    }
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => handleSearch(key, e.target.value, debouncedCallApi)}
    />
  );

  const columns = [
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Curriculum Name</div>
          {searchInput("curriculumName")}
        </div>
      ),
      dataIndex: "curriculumName",
      key: "curriculumName",
      sorter: (a, b) => a.curriculumName.localeCompare(b.curriculumName),
    },
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Content</div>
          {searchInput("content")}
        </div>
      ),
      dataIndex: "outlines",
      key: "content",
      width: 300,
      render: (outlines) => {
        if (!outlines?.length) return "-";
        const toTreeData = (nodes) =>
          nodes.map((node) => ({
            title: node.title,
            key: node.id || node.title,
            children: toTreeData(node.children || []),
          }));
        return (
          <Tree treeData={toTreeData(outlines)} defaultExpandkeys={["0-0"]} />
        );
      },
      sorter: (a, b) =>
        (a.outlineText || "").localeCompare(b.outlineText || ""),
    },
    // {
    //   title: () => (
    //     <div className="select-parent-div">
    //       <div className="special-table-header-text">Student Name</div>
    //       {searchInput("studentName")}
    //     </div>
    //   ),
    //   dataIndex: "studentName",
    //   key: "studentName",
    //   sorter: (a, b) => a.studentName.localeCompare(b.studentName),
    // },
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Status</div>
          {searchInput("status")}
        </div>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => status?.join(", ") || "-",
      sorter: (a, b) =>
        (a.status?.join(", ") || "").localeCompare(b.status?.join(", ") || ""),
    },
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Assign Date</div>
          {searchInput("created_on")}
        </div>
      ),
      dataIndex: "created_on",
      key: "created_on",
      render: (created_on) =>
        created_on ? moment(created_on).format("DD MMM YYYY hh:mm A") : "-",
      sorter: (a, b) => new Date(a.created_on) - new Date(b.created_on),
    },
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Due Date</div>
          {searchInput("due_on")}
        </div>
      ),
      dataIndex: "due_on",
      key: "due_on",
      render: (due_on) =>
        due_on ? moment(due_on).format("DD MMM YYYY hh:mm A") : "-",
      sorter: (a, b) => new Date(a.due_on) - new Date(b.due_on),
    },
    {
      title: () => (
        <div className="select-parent-div">
          <div className="special-table-header-text">Submit Date</div>
          {searchInput("submitted_on")}
        </div>
      ),
      dataIndex: "submitted_on",
      key: "submitted_on",
      render: (submitted_on) =>
        submitted_on ? moment(submitted_on).format("DD MMM YYYY hh:mm A") : "-",
      sorter: (a, b) => new Date(a.submitted_on) - new Date(b.submitted_on),
    },
  ];

  return (
    <div className="page-top-div">
      <span className="text-span">Homework History</span>
      <Button
        style={{ position: "absolute", right: "15mm", marginBottom: 20 }}
        onClick={() => navigate(-1)}
        icon={<BackIcon width={24} height={14} />}
      >
        Back
      </Button>
      <Row justify="center" className="production-manager-parent-row">
        <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
          <Row align="middle" gutter={[50, 0]}>
            <Col>
              <div>
                <span className="heading-span">Homework History</span>
              </div>
            </Col>
          </Row>
        </Col>

        <Col lg={24} xl={24} xxl={24} md={24} style={{ minHeight: "70vh" }}>
          <Form component={false} className="table-style">
            <Spin spinning={fetchHomeworksLoading}>
              <Table
                className="special-table activity-type-table project-statuses-table"
                rowClassName="editable-row"
                dataSource={filteredHomeworks}
                columns={columns}
                scroll={{ x: "100%" }}
                pagination={false}
                rowKey="_id"
              />
            </Spin>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default HomeworkHistory;
